import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Clock, MapPin, ChevronRight } from "lucide-react";
import { unitSuffix } from "@/lib/pricing";

const TIME_SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00", "18:30"];

export default function Booking() {
  const { serviceId } = useParams();
  const nav = useNavigate();
  const [svc, setSvc] = useState(null);
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/services/${serviceId}`).then(({ data }) => setSvc(data));
  }, [serviceId]);

  const submit = async () => {
    if (!date || !time || !address.trim()) {
      toast.error("Please pick a date, time and address");
      return;
    }
    setLoading(true);
    try {
      const scheduled = new Date(date);
      const [h, m] = time.split(":").map(Number);
      scheduled.setHours(h, m, 0, 0);
      const { data } = await api.post("/bookings", {
        service_id: serviceId,
        scheduled_at: scheduled.toISOString(),
        address,
        notes,
      });
      toast.success("Booking created. Complete payment to confirm.");
      nav(`/payment/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!svc) return <div className="py-16 text-center text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="booking-page">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">Step 1 of 2 · Schedule</div>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Confirm your booking</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-5">
          <div className="rounded-3xl border border-foreground/10 bg-white p-6">
            <Label className="text-sm font-semibold">Pick a date</Label>
            <div className="mt-3 flex justify-center rounded-2xl border p-2">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                fromDate={new Date()}
                data-testid="booking-calendar"
              />
            </div>
          </div>

          <div className="rounded-3xl border border-foreground/10 bg-white p-6">
            <Label className="text-sm font-semibold flex items-center gap-2"><Clock size={14} /> Time slot</Label>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="time-slots">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTime(t)}
                  data-testid={`slot-${t}`}
                  className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${
                    time === t ? "border-brand-ink bg-brand-ink text-white" : "border-foreground/10 bg-white text-foreground/70"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-foreground/10 bg-white p-6">
            <Label htmlFor="address" className="text-sm font-semibold flex items-center gap-2"><MapPin size={14} /> Service address</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Flat / Street / City / Pincode"
              className="mt-3 h-11"
              data-testid="booking-address"
            />
            <Label htmlFor="notes" className="mt-4 block text-sm font-semibold">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the provider should know..."
              className="mt-2"
              rows={3}
              data-testid="booking-notes"
            />
          </div>
        </div>

        {/* Summary */}
        <div>
          <div className="rounded-3xl border border-foreground/10 bg-white p-6 lg:sticky lg:top-24">
            <div className="flex items-center gap-3">
              <img src={svc.image_url || svc.category?.image_url} alt={svc.title} className="h-14 w-14 rounded-xl object-cover" />
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">{svc.category?.name}</div>
                <div className="font-heading text-sm font-bold">{svc.title}</div>
              </div>
            </div>
            <hr className="my-4" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service</span>
                <span>
                  ₹{svc.price}
                  {svc.pricing_unit && svc.pricing_unit !== "flat" && (
                    <span className="ml-1 text-xs text-muted-foreground">{unitSuffix(svc.pricing_unit)}</span>
                  )}
                </span>
              </div>
              <div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span>{svc.duration_minutes} min</span></div>
            </div>
            <hr className="my-4" />
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold">Total</span>
              <span className="font-heading text-2xl font-extrabold">
                ₹{svc.price}
                {svc.pricing_unit && svc.pricing_unit !== "flat" && (
                  <span className="ml-1 text-sm font-bold text-muted-foreground">{unitSuffix(svc.pricing_unit)}</span>
                )}
              </span>
            </div>
            <Button
              onClick={submit}
              disabled={loading}
              className="mt-6 h-12 w-full rounded-full bg-brand-orange text-white hover:bg-brand-orange/90"
              data-testid="proceed-to-payment"
            >
              {loading ? "Creating..." : (<>Proceed to UPI payment <ChevronRight size={16} /></>)}
            </Button>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">Booking is confirmed instantly after payment.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
